import { z } from 'zod'
import {
	CheckboxFieldSchema,
	EntityDateNullableSchema,
	EntityStringNullableSchema,
	IdSchema,
	StringSchema,
	ZipCodeSchema,
} from '#app/utils/zod-helpers.js'
import {
	USER_PROFILE_AGE_COHORT,
	USER_PROFILE_EDUCATION_LEVEL,
	USER_PROFILE_ETHNICITY,
	USER_PROFILE_GENDER,
	USER_PROFILE_POLITICAL_AFFILIATION,
	USER_PROFILE_PRONOUNS,
	USER_PROFILE_RELIGION,
	USER_PROFILE_ROUTINE,
	USER_PROFILE_RELATIONSHIP_STATUS,
	USER_PROFILE_SEXUAL_ORIENTATION,
	USER_PROFILE_PETS,
	USER_PROFILE_ALCOHOL_CONSUMPTION,
	USER_PROFILE_TRAVEL_DISTANCE,
	USER_PROFILE_AVAILABILITY_TIMES as ignoredUSER_PROFILE_AVAILABILITY_TIMES,
	USER_PROFILE_MONTHLY_HANGOUT_GOAL,
	USER_PROFILE_PLAN_AHEAD_WINDOW,
} from './user-profile._._definitions'

const BaseUserProfileSchema = z.object({
	bio: EntityStringNullableSchema('bio'),
	dob: EntityDateNullableSchema,
	ageCohort: z.nativeEnum(USER_PROFILE_AGE_COHORT),
	gender: z.nativeEnum(USER_PROFILE_GENDER),
	pronouns: z.nativeEnum(USER_PROFILE_PRONOUNS),
	sexualOrientation: z.nativeEnum(USER_PROFILE_SEXUAL_ORIENTATION),
	ethnicity: z.nativeEnum(USER_PROFILE_ETHNICITY),
	regionId: z.string().optional(),
	zipCode: ZipCodeSchema,
	educationLevel: z.nativeEnum(USER_PROFILE_EDUCATION_LEVEL),
	school: StringSchema('school', 1, 100),
	industry: StringSchema('industry', 1, 100),
	company: StringSchema('company', 1, 100),
	jobTitle: StringSchema('jobTitle', 1, 100),
	relationshipStatus: z.nativeEnum(USER_PROFILE_RELATIONSHIP_STATUS),
	religion: z.nativeEnum(USER_PROFILE_RELIGION),
	politicalAffiliation: z.nativeEnum(USER_PROFILE_POLITICAL_AFFILIATION),
	isVeteran: CheckboxFieldSchema,
	routine: z.nativeEnum(USER_PROFILE_ROUTINE),
	pets: z.nativeEnum(USER_PROFILE_PETS),
	homeStateOrCountry: StringSchema('homeStateOrCountry', 1, 100),
	partOfTown: StringSchema('partOfTown', 1, 100),
	alcoholConsumption: z.nativeEnum(USER_PROFILE_ALCOHOL_CONSUMPTION),
	// Geographic / travel fields
	address: z.string().max(200).nullable().optional(),
	city: z.string().max(100).nullable().optional(),
	state: z.string().max(50).nullable().optional(),
	distanceWillingToTravel: z.coerce.number().int().min(0).max(999).nullable().optional(),
	// New questionnaire fields
	travelDistance: z.nativeEnum(USER_PROFILE_TRAVEL_DISTANCE),
	availabilityTimes: z.string(), // JSON array
	workDescription: StringSchema('workDescription', 1, 500),
	learningInterests: StringSchema('learningInterests', 1, 500),
	activityRatings: z.string(), // JSON object
	desiredFriendAttributes: z.string(), // JSON array
	avoidedFriendAttributes: z.string(), // JSON array
	preferredBeverage: StringSchema('preferredBeverage', 1, 100),
	monthlyHangoutGoal: z.nativeEnum(USER_PROFILE_MONTHLY_HANGOUT_GOAL),
	hasKids: CheckboxFieldSchema,
	questionnaireCompletedAt: EntityDateNullableSchema,
	planAheadWindow: z.nativeEnum(USER_PROFILE_PLAN_AHEAD_WINDOW),
	maxHangsPerMonth: z.coerce.number().int().min(2).max(6).nullable().optional(),
})

export const UserProfileCreateSchema = BaseUserProfileSchema.extend({})

export const UserProfileUpdateSchema =
	BaseUserProfileSchema.partial().merge(IdSchema)
export type UserProfileUpdateInput = z.input<typeof UserProfileUpdateSchema>
